/**
 * Dispatcher for hover-preview body content shared across the four entity
 * badge hydration wrappers (`EntityBadgeById`, `EntityBadgeByIds`,
 * `EntityBadgeByEmail`, `EntityBadgeGroup`).
 *
 * Per-entity rendering lives on the entity type's registry entry under
 * `components.hoverBody`. Returns `null` for unsupported types — the
 * surrounding `HoverPreviewBadge` already short-circuits via
 * `SUPPORTED_HOVER_TYPES`, so this `null` is a defensive backstop in case
 * an unsupported type slips through (e.g. a caller that doesn't pass
 * `entityType` and the snapshot resolves to a non-supported type after
 * mount).
 *
 * See issue #5484 (Wire hover preview into entity badge wrappers) for the
 * original dispatch decision and #5483 / #5485 for the body and foundation.
 *
 * IMPORTANT — circular-dependency note:
 * This module intentionally does NOT statically import `entityTypeRegistry`.
 * Doing so would create a module-init TDZ cycle:
 *   entityTypeRegistry → NoteRecordOverview → NoteCard → MarkdownContent
 *   → entityRendering → EntityBadgeById → HoverPreviewBadge → hoverBodyDispatch
 *   → entityTypeRegistry  ← (cycle closes here, hangs Vitest)
 *
 * Instead, `entityTypeRegistry` injects its getter via `_setEntityTypeConfigGetter`
 * at the end of its own module body. By that time all modules have finished
 * initialising, so the getter is valid for every subsequent call to
 * `renderHoverBody` (which is only ever invoked at render time, not during
 * module evaluation).
 */

import type { ReactNode } from 'react';

import type { EntityTypeConfig } from '@/lib/crm/record/registry/entityTypeRegistry';
import type { CrmObject } from '@/types/crm';

type EntityTypeConfigGetter = (name: string) => EntityTypeConfig;

// Populated by entityTypeRegistry after it finishes loading.
// Null only in the brief window before the registry module evaluates;
// renderHoverBody returns null defensively in that window.
let _getEntityTypeConfig: EntityTypeConfigGetter | null = null;

// Dev-only guard: warn once if `renderHoverBody` is called before the
// registry has injected its getter. In practice every CRM surface that
// renders badges imports the registry transitively (via list configs or the
// slideout), so the getter is set before any badge mounts. This warning
// surfaces the issue immediately if a new entry point ever renders badges
// without first loading the registry.
let _warnedAboutMissingGetter = false;

/**
 * Called once by `entityTypeRegistry` at the end of its module body.
 * Breaks the circular import: hoverBodyDispatch no longer imports the
 * registry at module level.
 */
export function _setEntityTypeConfigGetter(fn: EntityTypeConfigGetter): void {
  _getEntityTypeConfig = fn;
}

/**
 * Render the hover-preview body for a given resolved CRM object.
 *
 * Returns `null` when the object or orgId isn't ready — callers pass this
 * through to `EntityBadgeWithHoverPreview` as `children`. The wrapper itself
 * already self-suppresses on `state.kind === 'loading' | 'failed'`, so this
 * `null` only matters during the brief window between badge mount and
 * snapshot resolution (or when the hydration wrapper has no orgId).
 *
 * Invariant: `entityTypeRegistry` must have been imported (and thus its
 * module body executed) by the time the first hover badge renders so the
 * getter is set. Every CRM page satisfies this via its list-config or
 * slideout imports. If you add a new entry point that renders badges
 * outside the CRM surfaces, import the registry from the page so its
 * module body runs at app load time.
 */
export function renderHoverBody(
  object: CrmObject | undefined,
  orgId: number | undefined
): ReactNode {
  if (!object || orgId === undefined) {
    return null;
  }
  if (_getEntityTypeConfig === null) {
    if (
      import.meta.env.DEV &&
      !_warnedAboutMissingGetter &&
      typeof console !== 'undefined'
    ) {
      _warnedAboutMissingGetter = true;
      console.warn(
        'hoverBodyDispatch: entityTypeRegistry has not initialized yet; ' +
          'hover previews will render empty until it loads. Ensure the entry ' +
          'point imports entityTypeRegistry (directly or transitively).'
      );
    }
    return null;
  }
  const HoverBody = _getEntityTypeConfig(object.entity_type_name)?.components
    ?.hoverBody;
  if (!HoverBody) {
    return null;
  }
  return <HoverBody object={object} orgId={orgId} />;
}
